import {
  ACCOUNT_BANNED_CODE,
  ACCOUNT_BANNED_MESSAGE,
  AuthError,
  type RefreshResult,
  SESSION_EXPIRED_MESSAGE,
} from './types'

/**
 * 時間をおいて再試行すれば通る可能性があるステータスか。
 * 5xx・429（レート制限）に加え、fetch が ok=false で返す 4xx 未満の異常値も
 * 原因を判定できないので誤ログアウトを避けてこちらに倒す。
 */
function isRetryableStatus(status: number): boolean {
  return status < 400 || status >= 500 || status === 429
}

/**
 * /auth/refresh の失敗レスポンスを分類する。
 *
 * - 5xx・429: 一時的な失敗。認証状態は保持して後で再試行する
 * - 403 ACCOUNT_BANNED: アカウント停止
 * - それ以外の 4xx: 何度送っても結果が変わらない＝確定的な認証失効
 *
 * backend はトークンの欠落・無効・期限切れをすべて 401 で返す（xrift-backend #602）が、
 * 401/403 だけを列挙すると別の 4xx が混じった時に「一時エラー扱いでユーザーを
 * クリアせず、ログイン表示のまま認証付き API だけ失敗し続ける」状態に固まる
 * （xrift-backend #601）。ステータスでなく「再試行で変わるか」で線を引く。
 */
export function classifyRefreshFailure(
  status: number,
  code?: string,
): Exclude<RefreshResult, 'refreshed'> {
  if (isRetryableStatus(status)) return 'transient-error'
  if (status === 403 && code === ACCOUNT_BANNED_CODE) return 'banned'
  return 'unauthorized'
}

/**
 * 認証付き API の失敗が「再ログインしないと直らない」ものか。
 *
 * 認証そのものの失敗（401・403・BAN）だけを確定とみなす。対象エンドポイント自体の
 * 404 や 400 はデプロイやプロキシの不調でも起きるので、ローカルセッションを捨てる
 * 理由にしない。通信断・5xx・リフレッシュの一時エラー・判定できない例外も同様。
 */
export function isDefinitiveAuthError(error: unknown): boolean {
  if (!(error instanceof AuthError)) return false
  return error.code === '401' || error.code === '403' || error.code === ACCOUNT_BANNED_CODE
}

/**
 * 確定的な失効のときにユーザーへ見せる文言。確定的でなければ null。
 * backend の生のエラー文や英語のフォールバック文言を画面に出さないため、
 * BAN とセッション切れの 2 種類に丸める。
 */
export function resolveDefinitiveAuthMessage(error: unknown): string | null {
  if (!isDefinitiveAuthError(error)) return null
  if ((error as AuthError).code === ACCOUNT_BANNED_CODE) return ACCOUNT_BANNED_MESSAGE
  return SESSION_EXPIRED_MESSAGE
}
